import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Coins, Gift, Clock, CheckCircle2, XCircle, Truck } from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { useT } from "@/lib/i18n";
import { celebrate } from "@/lib/celebrate";

const STATUS_BADGES = {
  pending: { label: "Pendiente", color: "bg-[#EAB308]/20 text-[#8C5E1A]", icon: Clock },
  approved: { label: "Aprobado", color: "bg-[#1B2E7A]/15 text-[#1B2E7A]", icon: CheckCircle2 },
  delivered: { label: "Entregado", color: "bg-[#4A7C59]/15 text-[#1B2E7A]", icon: Truck },
  rejected: { label: "Rechazado", color: "bg-[#DC2626]/15 text-[#DC2626]", icon: XCircle },
};

export default function Rewards() {
  const t = useT();
  const [rewards, setRewards] = useState([]);
  const [points, setPoints] = useState(0);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/rewards"),
        api.get("/points/me"),
        api.get("/me/redemptions"),
      ]);
      setRewards(r1.data);
      setPoints(r2.data.total);
      setRedemptions(r3.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const redeem = async (reward) => {
    if (!window.confirm(`¿Canjear "${reward.title}" por ${reward.cost_points} puntos?`)) return;
    try {
      await api.post(`/rewards/${reward.id}/redeem`);
      toast.success(`Canje enviado · -${reward.cost_points} puntos`);
      celebrate("redeem_reward");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo canjear");
    }
  };

  if (loading) return <div className="text-[#374151]">Cargando...</div>;

  return (
    <div data-testid="rewards-page">
      <div className="mb-8 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
          <Gift className="h-3.5 w-3.5" /> Reconocimiento
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Catálogo de Premios.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Cambia tus puntos Canales por premios reales. Acumulas puntos al aprobar exámenes,
          completar checklists y dominar el catálogo.
        </p>
      </div>

      <div className="bg-gradient-to-br from-[#1B2E7A] to-[#13205A] text-white rounded-md p-5 mb-10 inline-flex items-center gap-4">
        <Coins className="h-8 w-8 text-[#EAB308]" strokeWidth={1.5} />
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/60">Tu balance</p>
          <p className="font-heading text-3xl">{points.toLocaleString("es-MX")} pts</p>
        </div>
      </div>

      {rewards.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-md p-8 text-center text-[#737373] mb-10">
          Aún no hay premios disponibles. El admin puede agregarlos desde el panel.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {rewards.map((r) => {
            const canAfford = points >= r.cost_points;
            const outOfStock = r.stock === 0;
            return (
              <div
                key={r.id}
                data-testid={`reward-card-${r.id}`}
                className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden flex flex-col hover:shadow-md transition-all"
              >
                <div
                  className="aspect-[5/3] flex flex-col justify-between p-5 text-white relative"
                  style={{ background: `linear-gradient(135deg, ${r.color || "#1B2E7A"} 0%, ${r.color || "#13205A"}D9 100%)` }}
                >
                  <Gift className="h-8 w-8 text-white/80" strokeWidth={1.4} />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/70">Premio</p>
                    <h3 className="font-heading text-xl font-medium leading-tight">{r.title}</h3>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-[#374151] leading-relaxed mb-3 flex-1">{r.description}</p>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading text-2xl text-[#1B2E7A]">{r.cost_points.toLocaleString("es-MX")} <span className="text-xs text-[#6B7280]">pts</span></span>
                    {r.stock >= 0 && (
                      <span className="text-xs text-[#737373]">
                        {r.stock > 0 ? `${r.stock} disponibles` : "Agotado"}
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={() => redeem(r)}
                    disabled={!canAfford || outOfStock}
                    data-testid={`redeem-${r.id}`}
                    className="bg-[#1B2E7A] hover:bg-[#13205A] text-white w-full disabled:opacity-50"
                  >
                    {outOfStock ? "Agotado" : !canAfford ? `Faltan ${r.cost_points - points} pts` : "Canjear"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {redemptions.length > 0 && (
        <div>
          <SectionHeader
            eyebrow={t("rewards.eyebrow.history")}
            title={t("rewards.title.my_redemptions")}
            testid="section-rewards-history"
          />
          <div className="bg-white border border-[#D1D5DB] rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F3F4F6] text-[#374151] border-b-2 border-[#D1D5DB]">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">{t("rewards.col.reward")}</th>
                  <th className="text-left px-5 py-3 font-medium">{t("rewards.col.points")}</th>
                  <th className="text-left px-5 py-3 font-medium">{t("rewards.col.date")}</th>
                  <th className="text-left px-5 py-3 font-medium">{t("rewards.col.status")}</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r) => {
                  const sb = STATUS_BADGES[r.status] || STATUS_BADGES.pending;
                  const SI = sb.icon;
                  return (
                    <tr key={r.id} className="border-t border-[#E5E7EB]" data-testid={`my-redemption-${r.id}`}>
                      <td className="px-5 py-3 text-[#1A1A1A] font-medium">{r.reward_title}</td>
                      <td className="px-5 py-3 text-[#374151]">-{r.cost_points}</td>
                      <td className="px-5 py-3 text-[#737373] text-xs">
                        {new Date(r.created_at).toLocaleDateString("es-MX")}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md ${sb.color}`}>
                          <SI className="h-3 w-3" /> {sb.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
